"use client";

import React from "react";
import { CircleCheck, ArrowRight } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tabsData = [{
  id: "general",
  label: "Reconstructive"
}, {
  id: "cosmetic",
  label: "Esthetic Design"
}, {
  id: "surgical",
  label: "Advanced Surgery"
}];
const servicesData = [{
  id: 1,
  tabId: "general",
  title: "Full Mouth Rehabilitation",
  description: "Comprehensive restoration of the entire dentition to improve function, health, and aesthetic appearance.",
  price: "Expert Care"
}, {
  id: 2,
  tabId: "general",
  title: "Dental Implant Solutions",
  description: "Specialized replacement of missing teeth using state-of-the-art titanium or ceramic implant systems.",
  price: "Specialist Consultation"
}, {
  id: 3,
  tabId: "general",
  title: "Complex Crown & Bridge",
  description: "Precision-engineered fixed restorations designed to restore structural integrity and natural tooth form.",
  price: "Custom Design"
}, {
  id: 4,
  tabId: "cosmetic",
  title: "Artistic Smile Designing",
  description: "A tailored approach to creating your perfect smile using facial analysis and digital diagnostic mock-ups.",
  price: "Premier Service"
}, {
  id: 5,
  tabId: "cosmetic",
  title: "Porcelain Veneers",
  description: "Ultra-thin, custom-crafted porcelain shells that correct color, shape, and alignment for a flawless finish.",
  price: "Bespoke Result"
}, {
  id: 6,
  tabId: "cosmetic",
  title: "Gum Level Correction",
  description: "Micro-surgical techniques to reshape irregular gum lines and create a harmonious, balanced smile.",
  price: "Precision Care"
}, {
  id: 7,
  tabId: "surgical",
  title: "Advanced Bone Grafting",
  description: "Specialized procedures to augment jaw bone density, ensuring a stable foundation for dental implants.",
  price: "Clinical Excellence"
}, {
  id: 8,
  tabId: "surgical",
  title: "Surgical Extractions",
  description: "Expert removal of impacted or failing teeth with a focus on preserving surrounding bone and tissue.",
  price: "Pain Management"
}, {
  id: 9,
  tabId: "surgical",
  title: "Sinus Augmentation",
  description: "Sophisticated surgical elevation of the sinus floor to allow for successful implant placement in the upper jaw.",
  price: "Advanced Therapy"
}];
const benefitsData = [{
  serviceId: 1,
  text: "3D Digital workflow"
}, {
  serviceId: 1,
  text: "Occlusal harmony focus"
}, {
  serviceId: 2,
  text: "Biocompatible materials"
}, {
  serviceId: 2,
  text: "Long-term stability"
}, {
  serviceId: 3,
  text: "High-translucency ceramic"
}, {
  serviceId: 3,
  text: "Hand-finished detail"
}, {
  serviceId: 4,
  text: "Facial symmetry analysis"
}, {
  serviceId: 4,
  text: "Digital smile preview"
}, {
  serviceId: 5,
  text: "Minimally invasive prep"
}, {
  serviceId: 5,
  text: "Natural light reflection"
}, {
  serviceId: 6,
  text: "Laser-assisted precision"
}, {
  serviceId: 6,
  text: "Rapid healing protocols"
}, {
  serviceId: 7,
  text: "Regenerative membranes"
}, {
  serviceId: 7,
  text: "Enhanced integration"
}, {
  serviceId: 8,
  text: "Atraumatic technique"
}, {
  serviceId: 8,
  text: "Post-op comfort care"
}, {
  serviceId: 9,
  text: "Specialized microsurgery"
}, {
  serviceId: 9,
  text: "Predictable outcomes"
}];
export default function OurClinicalExpertise() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="85548" id="clinical-solutions" className="bg-[#18012C] py-24 sm:py-32 overflow-hidden dark">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-2xl text-center transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-[#C19B4A] text-3xl font-serif font-bold tracking-tight sm:text-4xl lg:text-5xl">
            Clinical Solutions
          </h2>
          <p className="text-white/70 mt-6 text-lg font-sans leading-8">Dr. Leclercq combines artistic vision with advanced prosthodontic mastery to solve complex dental challenges and restore the natural beauty of your smile.</p>
        </div>

        <div className="mt-16 sm:mt-20">
          <Tabs defaultValue="general" className="flex flex-col items-center w-full">
            <TabsList className="h-auto p-1.5 bg-white/5 rounded-2xl mb-12 flex-wrap justify-center gap-1">
              {tabsData.map((tab, index) => <TabsTrigger key={tab.id} value={tab.id} data-index={index} className="rounded-xl px-6 py-3 text-base font-sans font-medium transition-all data-[state=active]:bg-[#C19B4A] data-[state=active]:text-white data-[state=active]:shadow-lg">
                  {tab.label}
                </TabsTrigger>)}
            </TabsList>

            {tabsData.map((tab, tabIndex) => <TabsContent key={tab.id} value={tab.id} data-index={tabIndex} className="w-full focus-visible:outline-none focus-visible:ring-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {servicesData.filter(service => service.tabId === tab.id).map((service, serviceIndex) => <Card key={service.id} data-index={serviceIndex} className="border-white/10 shadow-xl shadow-black/20 rounded-[4px] bg-white/5 flex flex-col h-full transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(193,155,74,0.15)] group">
                        <CardHeader className="pb-4 pt-8 px-8">
                          <div className="flex justify-between items-start gap-4">
                            <CardTitle className="text-2xl font-serif font-bold text-[#C19B4A] leading-tight group-hover:text-[#C19B4A]">
                              {service.title}
                            </CardTitle>
                          </div>
                          <div className="inline-flex items-center rounded-full bg-white/5 px-3.5 py-1.5 text-xs font-sans font-semibold text-[#C19B4A] mt-4 w-fit tracking-wider uppercase">
                            {service.price}
                          </div>
                        </CardHeader>
                        
                        <CardContent className="px-8 flex-grow">
                          <CardDescription className="text-sm font-sans text-white/60 leading-relaxed">
                            {service.description}
                          </CardDescription>
                          
                          <ul className="mt-8 space-y-3.5">
                            {benefitsData.filter(benefit => benefit.serviceId === service.id).map((benefit, benefitIndex) => <li key={benefitIndex} data-index={benefitIndex} className="flex items-start gap-3">
                                  <CircleCheck className="h-4 w-4 shrink-0 text-[#C19B4A] mt-0.5" />
                                  <span className="text-xs font-sans font-medium text-white/80">{benefit.text}</span>
                                </li>)}
                          </ul>
                        </CardContent>
                        
                        <CardFooter className="px-8 pb-8 pt-6 mt-auto border-t border-white/10">
                          <Link to="#" className="w-full">
                            <Button variant="outline" className="w-full rounded-md h-12 text-[#C19B4A] border-[#C19B4A]/40 font-sans hover:bg-[#C19B4A] hover:text-white transition-colors group-hover:border-[#C19B4A]">
                              Book Consultation
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </Link>
                        </CardFooter>
                      </Card>)}
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}